import React from 'react';
import ContentReport from './ContentReport';
import { formatTime, SectionHeader, Verdict } from './reportPrimitives';

// P1 (2026-07-31): the hero number is stored as `confidence_score` but the formula is
// `100 − fillerRate×0.5 − wpmPenalty×0.3` — essentially "100 minus half your filler
// percentage". It measures fluency, not confidence: it contains no hedging, no
// self-undermining language, and no vocal signal, so a nervous, monotone, heavily
// hedging speaker who avoids "um" scores ~95. Renamed at the DISPLAY layer only — the
// stored key is untouched, so history and the progress trend stay comparable.
// Change this one string if a different label is preferred.
const HERO_SCORE_LABEL = 'Fluency score';

// Format the speaker's most-used terms from a { term: count } breakdown,
// e.g. `"probably", "maybe"`. Returns '' when there's nothing to show.
function topTerms(breakdown, limit = 3) {
  if (!breakdown) return '';
  return Object.entries(breakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([term]) => `"${term}"`)
    .join(', ');
}

// Per-occurrence log of a flagged term (filler or hedge) with context + timestamp.
function OccurrenceLog({ title, occurrences, accent }) {
  if (!occurrences?.length) return null;
  return (
    <div
      className="rounded-xl p-4"
      style={{ background: `${accent}0a`, border: `1px solid ${accent}26` }}
    >
      <SectionHeader accent={accent}>{title}</SectionHeader>
      <div className="space-y-2">
        {occurrences.map((occ, i) => (
          <div key={i} className="flex items-start gap-3">
            <span
              className="font-mono text-[10px] tabular-nums mt-0.5 flex-shrink-0"
              style={{ color: 'var(--text-faint)', minWidth: '2.5rem' }}
            >
              {formatTime(occ.timestamp_s) ?? `#${i + 1}`}
            </span>
            <p className="text-sm leading-snug" style={{ color: 'var(--text-muted)' }}>
              {occ.context.before && <span>…{occ.context.before} </span>}
              <span className="font-semibold px-0.5 rounded" style={{ color: accent, background: `${accent}1f` }}>
                {occ.context.match}
              </span>
              {occ.context.after && <span> {occ.context.after}…</span>}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const GRADE_MAP = [
  { min: 90, grade: 'A+', verdict: 'Outstanding delivery — everything clicked.' },
  { min: 80, grade: 'A',  verdict: 'Excellent control of language and pacing.' },
  { min: 70, grade: 'B+', verdict: 'Strong performance with minor rough edges.' },
  { min: 60, grade: 'B',  verdict: 'Solid speech with room to sharpen.' },
  { min: 50, grade: 'C+', verdict: 'Decent foundation — a few habits to break.' },
  { min: 40, grade: 'C',  verdict: 'Getting there — keep drilling the fundamentals.' },
  { min: 30, grade: 'D',  verdict: 'Several areas pulling the score down.' },
  { min: 0,  grade: 'F',  verdict: 'Needs significant work — start with filler words.' },
];

function ScoreHero({ score }) {
  if (score == null) return null;
  const { grade, verdict } = GRADE_MAP.find(g => score >= g.min) || GRADE_MAP[GRADE_MAP.length - 1];
  const color = score >= 70 ? 'var(--color-success)'
    : score >= 40 ? 'var(--accent)'
    : 'var(--color-danger)';
  const dimColor = score >= 70 ? 'var(--color-success-dim)'
    : score >= 40 ? 'var(--accent-dim)'
    : 'var(--color-danger-dim)';
  const borderColor = score >= 70 ? 'var(--color-success-border)'
    : score >= 40 ? 'var(--border-accent)'
    : 'var(--color-danger-border)';

  const R = 38;
  const CIRC = 2 * Math.PI * R;
  const dash = (score / 100) * CIRC;

  return (
    <div
      className="rounded-2xl p-5 flex items-center gap-5"
      style={{ background: dimColor, border: `1px solid ${borderColor}` }}
    >
      {/* Ring */}
      <div className="flex-shrink-0">
        <svg width="96" height="96" viewBox="0 0 96 96">
          <circle cx="48" cy="48" r={R} fill="none" stroke={borderColor} strokeWidth="4" />
          <circle
            cx="48" cy="48" r={R}
            fill="none"
            stroke={color}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${CIRC}`}
            transform="rotate(-90 48 48)"
          />
          <text x="48" y="42" textAnchor="middle" dominantBaseline="middle"
            fontSize="20" fontWeight="800" fontFamily="'Outfit', system-ui, sans-serif" fill={color}>
            {Math.round(score)}
          </text>
          <text x="48" y="60" textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="700" fontFamily="'Outfit', system-ui, sans-serif" fill={color}>
            {grade}
          </text>
        </svg>
      </div>
      {/* Verdict */}
      <div>
        <p className="font-bold text-base leading-snug" style={{ color: 'var(--text-primary)' }}>
          {verdict}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          {HERO_SCORE_LABEL}
        </p>
      </div>
    </div>
  );
}

export default function SpeechReport({ metrics, transcript, showTranscript = true }) {
  if (!metrics) return null;

  const {
    filler_words, hedging, duration_seconds, vocabulary,
    sentence_variance, pace_consistency, wpm, content,
  } = metrics;

  const avgWpm = pace_consistency?.avg_wpm ?? wpm ?? 0;
  const paceGood = avgWpm >= 100 && avgWpm <= 180;
  const paceScore = pace_consistency?.consistency_score;

  const fillerPerMin = filler_words?.per_minute != null
    ? filler_words.per_minute
    : duration_seconds > 0
      ? Math.round((filler_words?.count / (duration_seconds / 60)) * 10) / 10
      : null;
  const fillerGood = (filler_words?.count ?? 0) === 0;
  const fillerWarn = !fillerGood && (fillerPerMin == null || fillerPerMin < 2);

  const vocabGood = (vocabulary?.score ?? 0) >= 70;
  const vocabWarn = (vocabulary?.score ?? 0) >= 45 && (vocabulary?.score ?? 0) < 70;

  const sentGood = (sentence_variance?.score ?? 0) >= 70;
  const sentWarn = (sentence_variance?.score ?? 0) >= 45 && (sentence_variance?.score ?? 0) < 70;

  const hedgeCount = hedging?.count ?? 0;
  const hedgePerMin = hedging?.per_minute;
  const hedgeGood = hedgeCount === 0;
  const hedgeWarn = !hedgeGood && (hedgePerMin == null || hedgePerMin < 1.5);

  // The speaker's actual top filler / hedge words (not generic examples)
  const topFillers = topTerms(filler_words?.breakdown);
  const topHedges = topTerms(hedging?.breakdown);

  const heroScore = metrics.confidence_score ?? vocabulary?.score ?? null;

  return (
    <div className="space-y-5">

      {/* Grade reveal — emotional anchor before detail */}
      <ScoreHero score={heroScore} />

      {/* Content & ideas (Claude) — only when available */}
      {content && <ContentReport content={content} />}

      {/* Delivery + language verdicts */}
      <div
        className="rounded-xl p-4 space-y-3"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
      >
        <Verdict
          label={`Pace — ${avgWpm} WPM${paceScore != null ? ` · ${paceScore} consistency` : ''}`}
          good={paceGood && (paceScore == null || paceScore >= 70)}
          warning={paceGood && paceScore != null && paceScore < 70}
        >
          {!paceGood && avgWpm > 0
            ? avgWpm < 100
              ? 'Too slow — try to push the pace a little.'
              : 'Too fast — the audience will struggle to keep up.'
            : paceScore != null && paceScore < 70
              ? 'Your pace was uneven — some sections rushed, others dragged. Aim for steady variation.'
              : 'You spoke at a natural, comfortable speed.'}
        </Verdict>

        <Verdict
          label={`Filler Words — ${filler_words?.count ?? 0}${fillerPerMin != null ? ` (${fillerPerMin}/min)` : ''}`}
          good={fillerGood}
          warning={fillerWarn}
        >
          {fillerGood
            ? 'No filler words detected — excellent control.'
            : fillerWarn
              ? `${filler_words.count} fillers${topFillers ? ` (mostly ${topFillers})` : ''} — not bad, but see below for where they crept in.`
              : `${filler_words.count} fillers — ${fillerPerMin}/min${topFillers ? `, mostly ${topFillers}` : ''}. Focus on pausing instead of filling.`}
        </Verdict>

        {hedging != null && (
          <Verdict
            label={`Hedging — ${hedgeCount}${hedgePerMin != null ? ` (${hedgePerMin}/min)` : ''}`}
            good={hedgeGood}
            warning={hedgeWarn}
          >
            {hedgeGood
              ? 'No hedging language — you spoke with conviction.'
              : hedgeWarn
                ? `${hedgeCount} hedges${topHedges ? ` (mostly ${topHedges})` : ''} — a few crept in; see below.`
                : `${hedgeCount} hedges${topHedges ? ` (mostly ${topHedges})` : ''} weaken your authority. State claims directly.`}
          </Verdict>
        )}

        {vocabulary != null && (
          <Verdict label="Vocabulary" good={vocabGood} warning={vocabWarn}>
            {vocabGood
              ? `Rich word choice — ${vocabulary.unique_words} unique content words.`
              : vocabWarn
                ? 'Decent variety, though a few words carry a lot of weight.'
                : `Repetitive word choice — ${vocabulary.unique_words} unique out of ${vocabulary.total_words} content words. Vary your language.`}
          </Verdict>
        )}

        {sentence_variance != null && sentence_variance.avg_length > 0 && (
          <Verdict label="Sentence Variety" good={sentGood} warning={sentWarn}>
            {sentGood
              ? `Good mix — ${sentence_variance.short_count} short, ${sentence_variance.medium_count} medium, ${sentence_variance.long_count} long sentences.`
              : sentWarn
                ? 'Some variety, but sentences feel similar in length. Try punching in a short one after a long explanation.'
                : 'Sentences are too uniform in length — mix short punchy lines with longer ones to hold attention.'}
          </Verdict>
        )}
      </div>

      <OccurrenceLog title="Filler word log" occurrences={filler_words?.occurrences} accent="var(--accent)" />
      <OccurrenceLog title="Hedging log" occurrences={hedging?.occurrences} accent="var(--color-warning)" />

      {/* Filler crutch words — only the coachable repetition. Topic/brand/product
          words (top_repeated) are intentionally NOT shown here: repeating them is
          fine and varying them would hurt clarity. This matches what the AI coach
          acts on, so the report tells one consistent repetition story. */}
      {vocabulary?.top_crutches?.length > 0 && (
        <div
          className="rounded-xl p-4"
          style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-accent)' }}
        >
          <SectionHeader accent="var(--accent)">Filler crutch words</SectionHeader>
          <div className="flex flex-wrap gap-2">
            {vocabulary.top_crutches.map(({ word, count }) => (
              <span
                key={word}
                className="px-3 py-1 rounded-full text-sm font-medium font-mono"
                style={{ background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--border-accent)' }}
              >
                "{word}" × {count}
              </span>
            ))}
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--text-faint)' }}>
            These transition words add little — pause for a breath at those moments instead.
          </p>
        </div>
      )}

      {/* Transcript */}
      {showTranscript && transcript && (
        <div
          className="rounded-xl p-4"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
        >
          <SectionHeader>Transcript</SectionHeader>
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
            {transcript}
          </p>
        </div>
      )}
    </div>
  );
}
